/**
 * <s-product-gallery>
 * Swipeable gallery. Dots [data-thumb="slide-id"] jump to a slide, arrows [data-step="1|-1"]
 * move one slide; the visible slide marks its dot aria-current.
 */
class ProductGallery extends HTMLElement {
  #observer = null;
  #currentId = '';

  connectedCallback() {
    this.track = this.querySelector('[data-home-gallery]');
    if (!this.track) return;
    this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
    this.#currentId = this.slides[0]?.id || '';
    this.addEventListener('click', this.#onClick);
    this.#observer = new IntersectionObserver(this.#onIntersect, { root: this.track, threshold: 0.6 });
    this.slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
  }

  #goTo(slide) {
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const padding = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft - padding, behavior: reduce ? 'auto' : 'smooth' });
    this.#setCurrent(slide.id);
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const step = target.closest('[data-step]');
    if (step) {
      const index = this.slides.findIndex((slide) => slide.id === this.#currentId);
      const count = this.slides.length;
      const next = (index + Number(step.getAttribute('data-step')) + count) % count;
      this.#goTo(this.slides[next]);
      return;
    }
    const thumb = target.closest('[data-thumb]');
    if (thumb) this.#goTo(document.getElementById(thumb.getAttribute('data-thumb') || ''));
  };

  #onIntersect = (entries) => {
    const visible = entries.find((entry) => entry.isIntersecting);
    if (visible) this.#setCurrent(visible.target.id);
  };

  #setCurrent(id) {
    this.#currentId = id;
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      if (thumb.getAttribute('data-thumb') === id) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', ProductGallery);
